/**
 * 12-Week Total Fitness Foundations (Beginner Holistic)
 *
 * For users who pick the `general` goal at onboarding AND tag
 * themselves as `beginner`. The other beginner programs specialize —
 * Strength Foundations is barbell-only, First Mile is run-only, First
 * Mass is full-body lifting only. This is for people who don't want to
 * specialize: they want to feel good, move well, build a base across
 * all of strength + cardio + mobility, and not get hurt.
 *
 * Methodology — concurrent training at low-to-moderate intensity. The
 * research on concurrent strength + endurance training shows that
 * beginners can simultaneously develop both qualities (the so-called
 * "interference effect" matters mostly for advanced athletes who are
 * close to genetic ceilings). For a novice it's all upside.
 *
 * Structure:
 * - 4 days/week — sustainable for someone with a job, kids, etc.
 * - Day 1: Strength (full body, dumbbell-friendly)
 * - Day 2: Conditioning (light intervals + bodyweight circuit)
 * - Day 3: Strength (full body, second template — slight variation)
 * - Day 4: Long walk or easy run + mobility
 *
 * Loads stay light, intensity stays moderate. The goal isn't a PR — the
 * goal is showing up four days a week for twelve weeks. By the end the
 * user has the base to specialize if they want, or just keep cycling
 * this program for life-fitness maintenance.
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'total-fitness-12week',
  name: '12-Week Total Fitness Foundations',
  description: 'Build a base across strength, cardio, and mobility — four sustainable days a week. For people who want to feel good and move well rather than specialize. The starting line for everything else.',
  level: 'recruit',
  duration: 12,
  daysPerWeek: 4,
  goals: ['general'],
  difficulty: 'beginner',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
};

const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    // W1 + W12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Fitness Testing',
      focus: 'Bodyweight markers + light-load benchmarks + 1-mile walk-jog for time',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
    // Weeks 2-5: Foundation. Low-moderate intensity across all
    // qualities. The point is showing up consistently.
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Foundation Phase',
      focus: 'Build the habit. Light loads, easy cardio, daily mobility.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.55,
        endIntensity: 0.65,
        weeklyIncrease: 0.025,
      },
      trainingParameters: {
        repRange: [10, 12],
        setsMultiplier: 1.0,
        restPeriod: '60-90s',
      },
    },
    // Week 6: Easy Week. Lower intensity, same routine. Lets users
    // who pushed too hard catch up.
    {
      weeks: [6],
      type: 'deload',
      name: 'Easy Week',
      focus: 'Drop loads 15%, keep showing up. Reset.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.85,
      progression: {
        type: 'linear',
        startIntensity: 0.60,
        endIntensity: 0.60,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [8, 10],
        setsMultiplier: 0.85,
        restPeriod: '90s',
      },
    },
    // Weeks 7-10: Build Phase. Loads rise modestly, conditioning gets
    // a bit harder. Still sustainable.
    {
      weeks: [7, 8, 9, 10],
      type: 'accumulation',
      name: 'Build Phase',
      focus: 'Moderate loads, conditioning intervals, longer aerobic on Day 4.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.05,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.75,
        weeklyIncrease: 0.025,
      },
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 1.05,
        restPeriod: '60-90s',
      },
    },
    // Week 11: Light week before retest.
    {
      weeks: [11],
      type: 'deload',
      name: 'Light Week',
      focus: 'Cut volume 30%. Keep moving without grinding.',
      intensityMultiplier: 0.9,
      volumeMultiplier: 0.7,
      progression: {
        type: 'linear',
        startIntensity: 0.70,
        endIntensity: 0.70,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [8, 10],
        setsMultiplier: 0.7,
        restPeriod: '90s',
      },
    },
  ],
};

// BASE TEMPLATES

const testingDay: WorkoutTemplateDay = {
  name: 'Fitness Test',
  sessionType: 'assessment',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open hips before testing' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Wake up upper back' },
      ],
    },
    {
      name: 'Bodyweight Markers',
      type: 'testing',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Max strict reps. Knees down OK if needed.', sessionType: 'assessment' },
        { name: 'Plank Hold', sets: 1, reps: 'Max Time', format: 'bodyweight', details: 'Stop when form breaks.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Light Load + Cardio',
      type: 'testing',
      exercises: [
        { name: 'Goblet Squat', sets: 1, reps: '10RM', intensity: 'Tech', details: '10 perfect reps with the heaviest dumbbell you can clean rep.', sessionType: 'assessment' },
        { name: 'Run', duration: '1 mile', format: 'cardio', intensity: 'Hard', details: 'W1: walk-jog it for time. W12: same — note improvement.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 8, format: 'recovery', details: 'Decompress' },
      ],
    },
  ],
};

// Day 1 — Strength A. Lower-body bias, full-body coverage.
const strengthADay: WorkoutTemplateDay = {
  name: 'Strength A',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility before loading' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Hips before squatting' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Upper back activation' },
      ],
    },
    {
      name: 'Full Body',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 10, intensity: 'progressive', details: 'Dumbbell or kettlebell at chest. Foundation squat.' },
        { name: 'Romanian Deadlift', sets: 3, reps: 10, intensity: 'progressive', details: 'Hinge with light weight. Slight knee bend, neutral spine.' },
        { name: 'Push-Ups', sets: 3, reps: 8, format: 'bodyweight', details: 'Strict reps. Knees down if needed — progress to standard then to deficit over the program.' },
        { name: 'Inverted Row', sets: 3, reps: 8, format: 'bodyweight', details: 'Bar at hip height. More upright = easier; body parallel to floor = harder.' },
        { name: 'Plank Hold', sets: 2, reps: '30s', format: 'bodyweight', details: 'Anti-extension core. Brace abs, glutes squeezed.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 8, format: 'recovery', details: 'Open hips and decompress' },
      ],
    },
  ],
};

// Day 2 — Conditioning. Light intervals + a short bodyweight circuit.
// Builds aerobic and anaerobic capacity without crushing the lifting
// days that bracket it.
const conditioningDay: WorkoutTemplateDay = {
  name: 'Conditioning',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Hips before any running' },
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Brisk walk to warm up' },
      ],
    },
    {
      name: 'Intervals',
      type: 'conditioning',
      format: 'intervals',
      exercises: [
        { name: 'Interval Run', sets: 6, reps: '60s run / 90s walk', format: 'intervals', details: 'Run hard for 60 seconds, walk for 90. Repeat 6 times. Total ~15 minutes.' },
      ],
    },
    {
      name: 'Bodyweight Circuit',
      type: 'circuit',
      format: 'circuit',
      exercises: [
        { name: 'Walking Lunge', sets: 3, reps: 10, format: 'circuit', details: 'Per leg. Bodyweight or light dumbbells.' },
        { name: 'Burpees', sets: 3, reps: 8, format: 'circuit', details: 'Controlled tempo. Step back instead of jumping if needed.' },
        { name: 'Plank Hold', sets: 3, reps: '30s', format: 'circuit', details: 'Between rounds — active rest.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Walk it out. Heart rate drops.' },
      ],
    },
  ],
};

// Day 3 — Strength B. Slight variation from Strength A so the body
// adapts to a wider movement range. Same patterns covered — squat,
// hinge, push, pull, core.
const strengthBDay: WorkoutTemplateDay = {
  name: 'Strength B',
  sessionType: 'pt',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Hips before lunging' },
        { name: 'Band Pull-Aparts', sets: 2, reps: 15, format: 'recovery', details: 'Upper back activation' },
      ],
    },
    {
      name: 'Full Body',
      type: 'strength',
      exercises: [
        { name: 'Walking Lunge', sets: 3, reps: 10, intensity: 'progressive', details: 'Per leg. Bodyweight first; add dumbbells when 10/leg is easy.' },
        { name: 'Hip Thrust', sets: 3, reps: 12, intensity: 'progressive', details: 'Bench against your back. Pause at top, squeeze glutes.' },
        { name: 'Dumbbell Bench Press', sets: 3, reps: 10, intensity: 'progressive', details: 'Lying on a bench. Controlled descent, full press.' },
        { name: 'Dumbbell Row', sets: 3, reps: 10, intensity: 'progressive', details: 'One arm at a time, brace against bench.' },
        { name: 'Side Plank', sets: 2, reps: '20s', format: 'bodyweight', details: 'Per side. Lateral core stability.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 8, format: 'recovery', details: 'Open hips and decompress' },
      ],
    },
  ],
};

// Day 4 — Long aerobic + mobility. Walking or easy jogging plus a
// solid mobility flow to undo the desk-job posture.
const aerobicMobilityDay: WorkoutTemplateDay = {
  name: 'Long Aerobic + Mobility',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Long Aerobic',
      type: 'conditioning',
      exercises: [
        { name: 'Easy Run', duration: '30-45 min', format: 'cardio', intensity: 'Easy', details: 'Walk, easy run, hike, or bike — pick what feels good. Conversational pace, RPE 5. The longest single effort of the week.' },
      ],
    },
    {
      name: 'Mobility',
      type: 'recovery',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 3, reps: 10, format: 'recovery', details: 'Slow spine articulation' },
        { name: 'Hip Opener Flow', sets: 3, reps: 10, format: 'recovery', details: 'Hips, glutes, calves' },
        { name: 'Band Pull-Aparts', sets: 3, reps: 15, format: 'recovery', details: 'Posture insurance' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const totalFitness12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12)
    'testing-week-day1': testingDay,
    'testing-week-day2': restDay,
    'testing-week-day3': restDay,
    'testing-week-day4': restDay,
    'testing-week-day5': restDay,
    'testing-week-day6': restDay,
    'testing-week-day7': restDay,

    // Training cycle — 4 days/week
    'training-day1': strengthADay,
    'training-day2': conditioningDay,
    'training-day3': strengthBDay,
    'training-day4': restDay,
    'training-day5': aerobicMobilityDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};
